import { BusEventWithPayload } from '@grafana/data';
import { getAppEvents } from '@grafana/runtime';

/**
 * Payload for the InvestigationCreatedEvent
 */
export interface InvestigationCreatedPayload {
  /** The ID of the newly created investigation */
  investigationId: string;
}

/**
 * Event emitted when an investigation is created.
 * External integrations can subscribe to this event to react to new investigations.
 */
export class InvestigationCreatedEvent extends BusEventWithPayload<InvestigationCreatedPayload> {
  static type = 'grafana-assistant:investigation-created';
}

/**
 * Emit an InvestigationCreatedEvent to notify subscribers that a new investigation was created.
 *
 * @param investigationId - The ID of the newly created investigation
 */
export function emitInvestigationCreated(investigationId: string): void {
  const event = new InvestigationCreatedEvent({ investigationId });
  getAppEvents().publish(event);
}

/**
 * Subscribe to investigation created events.
 *
 * @param callback - Function to call when an investigation is created
 * @returns Unsubscribe function to stop listening for events
 *
 * @example
 * ```typescript
 * import { onInvestigationCreated } from '@grafana/assistant';
 *
 * const unsubscribe = onInvestigationCreated((payload) => {
 *   console.log('Investigation created:', payload.investigationId);
 * });
 *
 * // Later, when you want to stop listening
 * unsubscribe();
 * ```
 */
export function onInvestigationCreated(callback: (payload: InvestigationCreatedPayload) => void): () => void {
  const subscription = getAppEvents().subscribe(InvestigationCreatedEvent, (event) => {
    callback(event.payload);
  });

  return () => subscription.unsubscribe();
}
